import type { Params } from '@angular/router';
import type { Action } from '@ngrx/store';
import { forkJoin, of } from 'rxjs';
import type { SiteEntity } from '@common/types/site.type';
import type { SitePageEntity } from '@common/types/site-page.type';

import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';

import { UserPlatform, EAppMode } from '@common/enums';
import { EEnvTypes } from '@common/enums/env-types.enum';
import { TranslationLoaderService } from '@common/services/translation-loader.service';

import { environment } from '@web-env/environment';

import { SiteService } from '@web/store/website/site/site.service';
import { PagesService } from '@web/store/website/pages/pages.service';

import { SiteHttpService } from '@web/store/website/site/site.http.service';
import { PagesHttpService } from '@web/store/website/pages/pages.http.service';
import { TemplatesHttpService } from '@web/core/services/REST/tempates.http.service';
import { LibraryBlocksHttpService } from '@web/store/library-blocks/library-blocks.http.service';
import { SessionHttpService } from '@web/core/services/REST/session.http.service';

import { RedirectFacade } from '@common/utils/redirect/redirect.facade';
import { AppFacade } from '@web/store/app/app.facade';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { ContentFacade } from '@web/store/website/content/content.facade';
import { RouterFacade } from '@web/store/router/router.facade';

import AppActions from '@web/store/app/app.actions';
import SiteActions from '@web/store/website/site/site.actions';
import PagesActions from '@web/store/website/pages/pages.actions';
import ContentActions from '@web/store/website/content/content.actions';
import * as LibraryBlocksActions from '@web/store/library-blocks/library-blocks.actions';
import { DataLayerHttpService } from '@web/core/services/REST/data-layer.http.service';

@Injectable()
export class AppEffects {
    private readonly actions$ = inject(Actions);
    private readonly cookieService = inject(CookieService);
    private readonly ngxUiLoaderService = inject(NgxUiLoaderService);
    private readonly appFacade = inject(AppFacade);
    private readonly siteFacade = inject(SiteFacade);
    private readonly contentFacade = inject(ContentFacade);
    private readonly routerFacade = inject(RouterFacade);
    private readonly redirectFacade = inject(RedirectFacade);
    private readonly siteService = inject(SiteService);
    private readonly pagesService = inject(PagesService);
    private readonly siteHttpService = inject(SiteHttpService);
    private readonly pagesHttpService = inject(PagesHttpService);
    private readonly templatesHttpService = inject(TemplatesHttpService);
    private readonly libraryBlockHttpService = inject(LibraryBlocksHttpService);
    private readonly sessionHttpService = inject(SessionHttpService);
    private readonly dataLayerHttpService = inject(DataLayerHttpService);

    public readonly init$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.initApp),
            withLatestFrom(
                this.routerFacade.routerParams$,
                this.routerFacade.routerQueryParams$,
                this.routerFacade.routerUrl$,
                this.appFacade.isAdmin$,
                (_, routerParams: Params, routerQueryParams: Params, routerUrl: string, isAdmin: boolean) => ({
                    routerParams,
                    routerQueryParams,
                    routerUrl,
                    isAdmin,
                })
            ),
            map(({ routerParams, routerQueryParams, routerUrl, isAdmin }) => {
                if (isAdmin) {
                    return AppActions.initAdmin();
                }

                if (this.siteService.isEcommerceMode(routerParams, routerUrl)) {
                    return AppActions.initEcommerce();
                }

                if (this.siteService.isNewSiteFromTemplate(routerParams, routerQueryParams)) {
                    return AppActions.initNewSiteFromTemplate();
                }

                if (this.siteService.isNewPageFromTemplate(routerParams, routerQueryParams)) {
                    return AppActions.initNewPageFromTemplate();
                }

                if (this.siteService.isNewSite(routerParams)) {
                    return AppActions.initNewSite();
                }

                if (this.siteService.isNewPage(routerParams)) {
                    return AppActions.initNewPage();
                }

                return AppActions.initSite();
            })
        )
    );

    public readonly initAdmin$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.initAdmin),
            withLatestFrom(this.routerFacade.routerParams$, (_, routerParams: Params) => routerParams),
            mergeMap((routerParams) =>
                forkJoin({
                    template: this.templatesHttpService.getAdminTemplate(routerParams.templateId),
                    libraryBlocks: this.libraryBlockHttpService.getLibraryBlocks(routerParams.templateId),
                })
            ),
            switchMap(({ template, libraryBlocks }) => {
                return [
                    AppActions.initAppRequest({ template: this.siteService.prepareTemplate(template) }),
                    LibraryBlocksActions.loadLibraryBlocksSuccess({ libraryBlocks }),
                ];
            })
        )
    );

    public readonly initEcommerce$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.initEcommerce),
            withLatestFrom(
                this.routerFacade.routerParams$,
                this.routerFacade.routerQueryParams$,
                (_, routerParams: Params, routerQueryParams: Params) => ({ routerParams, routerQueryParams })
            ),
            mergeMap(({ routerParams }) =>
                forkJoin({
                    site: this.siteHttpService.getSite(routerParams.siteId),
                    productPage: this.pagesHttpService.getSitePage(routerParams.siteId, 'product'),
                })
            ),
            mergeMap(({ productPage, site }) => {
                if (!site || !productPage) {
                    window.location.href = 'https://login.sendpulse.com/404';
                    return [];
                }

                return [
                    AppActions.setMode({ mode: EAppMode.ECOMMERCE }),
                    AppActions.initAppRequest({ site, pages: [productPage], currentPage: productPage, total: 1 }),
                    ContentActions.loadContent({ siteId: site.id, pageId: productPage.id }),
                ];
            })
        )
    );

    public readonly initSite$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.initSite),
            withLatestFrom(this.routerFacade.routerParams$, (_, routerParams: Params) => routerParams),
            switchMap((routerParams) =>
                forkJoin({
                    site: this.siteHttpService.getSite(routerParams.siteId),
                    data: this.pagesHttpService.getSitePages(routerParams.siteId),
                    routerParams: of(routerParams),
                    selectedPage: this.pagesHttpService.getSitePage(routerParams.siteId, routerParams.pageId),
                })
            ),
            switchMap(({ site, data, routerParams, selectedPage }) => {
                if (!site || !data || !selectedPage) {
                    window.location.href = 'https://login.sendpulse.com/404';
                    return [];
                }

                if (this.pagesService.isPageContentSourceMiniLanding(selectedPage.content_source_id)) {
                    window.location.href = `/constructor/${site.id}/page/${selectedPage.id}`;
                    return [];
                }

                const pages = data.pages;
                const isPageInLoadedPages = selectedPage
                    ? pages.some((page) => page.id.toString() === selectedPage.id.toString())
                    : false;

                if (!isPageInLoadedPages) {
                    pages.push(selectedPage);
                }

                const currentPage = this.pagesService.findCurrentPage(pages, routerParams);

                return [
                    AppActions.initAppRequest({ site, ...{ pages: pages }, currentPage, total: data.total }),
                    ContentActions.loadContent({ siteId: site.id, pageId: currentPage.id }),
                ];
            })
        )
    );

    public readonly initNewSite$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.initNewSite),
            map(() => ({
                site: this.siteService.createEmptySite() as SiteEntity,
                page: this.pagesService.createEmptyPage() as SitePageEntity,
            })),
            switchMap(({ site, page }) => [
                AppActions.initAppRequest({ site, pages: [page], currentPage: page }),
                ContentActions.loadContent({ siteId: site.id, pageId: page.id }),
            ])
        )
    );

    public readonly initNewPage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.initNewPage),
            withLatestFrom(this.routerFacade.routerParams$, (_, routerParams: Params) => routerParams),
            switchMap((routerParams) =>
                forkJoin({
                    site: this.siteHttpService.getSite(routerParams.siteId),
                    data: this.pagesHttpService.getSitePages(routerParams.siteId),
                })
            ),
            switchMap(({ site, data }) => {
                const newPage = this.pagesService.createEmptyPage() as SitePageEntity;
                return [
                    AppActions.initAppRequest({
                        site,
                        pages: data?.pages ? [...data.pages, newPage] : [newPage],
                        currentPage: newPage,
                        total: data?.total || 1,
                    }),
                    ContentActions.loadContent({ siteId: site?.id, pageId: newPage.id }),
                ];
            })
        )
    );

    public readonly initNewSiteFromTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.initNewSiteFromTemplate),
            withLatestFrom(this.routerFacade.routerQueryParams$, (_, routerQueryParams: Params) => routerQueryParams),
            mergeMap((routerQueryParams: Params) =>
                forkJoin({
                    template: this.templatesHttpService.getTemplate(routerQueryParams.templateId),
                })
            ),
            switchMap(({ template }) => {
                const site = this.siteService.createEmptySite() as SiteEntity;
                const page = this.pagesService.createEmptyPage() as SitePageEntity;
                return [
                    AppActions.initAppRequest({ site, pages: [page], currentPage: page }),
                    ContentActions.loadContent({ siteId: site.id, pageId: page.id, template }),
                ];
            })
        )
    );

    public readonly initNewPageFromTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.initNewPageFromTemplate),
            withLatestFrom(
                this.routerFacade.routerParams$,
                this.routerFacade.routerQueryParams$,
                (_, routerParams: Params, routerQueryParams: Params) => ({ routerParams, routerQueryParams })
            ),
            switchMap(({ routerParams, routerQueryParams }) =>
                forkJoin({
                    site: this.siteHttpService.getSite(routerParams.siteId),
                    data: this.pagesHttpService.getSitePages(routerParams.siteId),
                    template: this.templatesHttpService.getTemplate(routerQueryParams.templateId),
                })
            ),
            switchMap(({ site, data, template }) => {
                const page = this.pagesService.createEmptyPage() as SitePageEntity;
                return [
                    AppActions.initAppRequest({
                        site,
                        pages: [...data.pages, page],
                        currentPage: page,
                        total: data.total,
                    }),
                    ContentActions.loadContent({ siteId: site.id, pageId: page.id, template }),
                ];
            })
        )
    );

    public readonly initAppRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.initAppRequest),
            switchMap(({ site, pages, currentPage, template, total }) => {
                const actions: Action[] = [AppActions.initAppSuccess()];
                if (site) {
                    actions.push(
                        PagesActions.loadPagesSuccess({
                            pages: this.pagesService.mapPagesToEntityPages(pages),
                            total: total,
                        })
                    );
                    actions.push(SiteActions.loadSiteSuccess({ site, currentPage }));
                }
                if (template) {
                    actions.push(SiteActions.loadTemplateSuccess({ template }));
                }
                return actions;
            })
        )
    );

    public readonly setAppLocal$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.initApp),
            map(() => AppActions.setLocale({ locale: TranslationLoaderService.getCurrentLocale(this.cookieService) }))
        )
    );

    public readonly initExit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.initExit),
            withLatestFrom(
                this.contentFacade.isDirty$,
                this.siteFacade.isSaving$,
                this.appFacade.isAdmin$,
                this.routerFacade.routerParams$,
                (
                    { isRedirectTabClose },
                    isDirty: boolean,
                    isSaving: boolean,
                    isAdmin: boolean,
                    routerParams: Params
                ) => ({
                    isRedirectTabClose,
                    isDirty,
                    isSaving,
                    isAdmin,
                    routerParams,
                })
            ),
            filter(({ isSaving, isAdmin }) => isAdmin || !isSaving),
            map(({ isRedirectTabClose, isDirty, routerParams }) => {
                if (!isDirty) {
                    return AppActions.exit({ isRedirectTabClose });
                }

                return SiteActions.saveSiteRequest({
                    exit: true,
                    publish: false,
                    isRedirectTabClose,
                    capturedPageId: routerParams.pageId,
                });
            })
        )
    );

    public readonly exit$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AppActions.exit),
                withLatestFrom(
                    this.routerFacade.routerParams$,
                    this.appFacade.isAdmin$,
                    ({ isRedirectTabClose }, routerParams: Params, isAdmin: boolean) => ({
                        isRedirectTabClose,
                        routerParams,
                        isAdmin,
                    })
                ),
                map(({ isRedirectTabClose, routerParams, isAdmin }) => {
                    let url;
                    if (isAdmin) {
                        url = '/admin/landings/websites-templates/';
                    } else if (this.siteService.isNewSite(routerParams, isAdmin)) {
                        url = `/landing-pages/${isRedirectTabClose ? '?redirect=constructor' : ''}`;
                    } else {
                        url = `/landing-pages/sites/${routerParams.siteId}${
                            isRedirectTabClose ? '?redirect=constructor' : ''
                        }`;
                    }
                    this.redirectFacade.redirectAction(url);
                })
            ),
        { dispatch: false }
    );

    public readonly setPlatform$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.initApp),
            map(() => {
                const platform = navigator?.platform;

                if (platform?.toLowerCase().includes('mac')) {
                    return AppActions.setPlatform({ platform: UserPlatform.mac });
                }

                if (platform?.toLowerCase().includes('win')) {
                    return AppActions.setPlatform({ platform: UserPlatform.windows });
                }

                if (platform?.toLowerCase().includes('linux')) {
                    return AppActions.setPlatform({ platform: UserPlatform.linux });
                }

                return AppActions.setPlatform({ platform: UserPlatform.unknown });
            })
        )
    );

    public readonly updateSession$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(AppActions.updateSession),
                tap(() => this.sessionHttpService.updateSession().subscribe())
            );
        },
        { dispatch: false }
    );

    public readonly dataLayerHandler$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.dataLayerRequest),
            concatLatestFrom(() => this.appFacade.isAdmin$),
            filter(([, isAdmin]) => !isAdmin),
            switchMap(() => this.dataLayerHttpService.getDataLayer()),
            map((response) => AppActions.setDataLayer({ dataLayer: response?.dataLayer ?? null }))
        )
    );

    constructor() {
        if ([EEnvTypes.DEV].includes(environment.type) && !navigator.onLine) {
            return;
        }
        this.appFacade.isReady$.subscribe((isReady: boolean) => {
            return isReady ? this.ngxUiLoaderService.stopLoader('main') : this.ngxUiLoaderService.startLoader('main');
        });
    }
}
